<template>
  <div class="home-nav-slot">
    <header class="home-nav">
      <div class="home-nav__bar">
        <router-link
          to="/"
          class="home-nav__brand"
          aria-label="Escribonauta"
          @click="goHome"
        >
          <span class="home-nav__mark" aria-hidden="true">
            <img src="/icon-192.png" alt="" />
          </span>
          <span class="home-nav__brand-name">Escribonauta</span>
        </router-link>

        <button
          type="button"
          class="home-nav__toggle"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-controls="home-nav-menu"
          @click="toggleMenu"
        >
          <v-icon size="24" color="white">{{ menuOpen ? 'mdi-close' : 'mdi-menu' }}</v-icon>
          <span class="home-nav__toggle-label">Menú</span>
        </button>

        <nav
          id="home-nav-menu"
          class="home-nav__links"
          :class="{ 'home-nav__links--open': menuOpen }"
          aria-label="Secciones del inicio"
        >
          <a href="/#selector" class="home-nav__link" @click.prevent="goToSection('selector')">
            ¿Cómo funciona?
          </a>
          <a href="/#precios" class="home-nav__link" @click.prevent="goToSection('precios')">
            Precios
          </a>
          <router-link to="/preguntas-frecuentes" class="home-nav__link" @click="closeMenu">
            Preguntas frecuentes
          </router-link>
          <router-link to="/proximamente" class="home-nav__link" @click="closeMenu">
            Próximamente
          </router-link>
        </nav>

        <div ref="entry" class="home-nav__entry">
          <button
            type="button"
            class="home-nav__cta"
            aria-haspopup="menu"
            :aria-expanded="entryOpen ? 'true' : 'false'"
            @click="toggleEntry"
          >
            Ingresar
            <v-icon size="18" :class="{ 'home-nav__caret--open': entryOpen }">mdi-chevron-down</v-icon>
          </button>
          <div v-if="entryOpen" class="home-nav__dropdown" role="menu">
            <button type="button" role="menuitem" @click="onStart">Comenzar sin registro</button>
            <button type="button" role="menuitem" @click="onLogin">Iniciar sesión</button>
            <button type="button" role="menuitem" @click="onRegister">Registrarme</button>
          </div>
        </div>
      </div>
    </header>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { navigateToLandingSection } from '@/utils/landingHomeNavigation'

export default defineComponent({
  name: 'HomeNavBar',

  emits: {
    'start-writing': () => true,
    login: () => true,
    register: () => true,
  },

  data() {
    return {
      menuOpen: false,
      entryOpen: false,
    }
  },

  watch: {
    '$route.fullPath'() {
      this.scrollToHomeHash()
    },
  },

  mounted() {
    this.scrollToHomeHash()
    document.addEventListener('click', this.onDocumentClick)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick)
  },

  methods: {
    closeMenu() {
      this.menuOpen = false
      this.entryOpen = false
    },

    toggleMenu() {
      this.menuOpen = !this.menuOpen
      if (!this.menuOpen) this.entryOpen = false
    },

    toggleEntry() {
      this.entryOpen = !this.entryOpen
    },

    onDocumentClick(event: MouseEvent) {
      const root = this.$refs.entry as HTMLElement | undefined
      if (!root || !event.target || root.contains(event.target as Node)) return
      this.entryOpen = false
    },

    onStart() {
      this.closeMenu()
      this.$emit('start-writing')
    },

    onLogin() {
      this.closeMenu()
      this.$emit('login')
    },

    onRegister() {
      this.closeMenu()
      this.$emit('register')
    },

    goHome(event: MouseEvent) {
      this.closeMenu()
      event.preventDefault()
      const scrollTop = () => window.scrollTo(0, 0)
      if (this.$route.fullPath === '/') {
        scrollTop()
        return
      }
      this.$router.push('/').then(scrollTop)
    },

    goToSection(id: string) {
      this.closeMenu()
      navigateToLandingSection(this.$router, id)
    },

    scrollToHomeHash() {
      if (this.$route.path !== '/') return
      const id = this.$route.hash.replace('#', '')
      if (!id) return
      this.scrollToId(id)
    },

    scrollToId(id: string) {
      this.$nextTick(() => {
        document.getElementById(id)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    },
  },
})
</script>

<style scoped>
.home-nav-slot {
  height: 4rem;
}

.home-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 30;
  height: 4rem;
  background: #1d3557;
  color: white;
}

.home-nav__bar {
  max-width: 1200px;
  height: 100%;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.home-nav__brand {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: white;
  text-decoration: none;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  flex-shrink: 0;
}

.home-nav__mark {
  display: block;
  width: 2.15rem;
  height: 2.15rem;
  overflow: hidden;
  border-radius: 9px;
  background: white;
  flex-shrink: 0;
}

.home-nav__mark img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.home-nav__toggle {
  display: none;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.2rem;
  border: 0;
  background: transparent;
  color: white;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.home-nav__links {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1.65rem;
  margin-left: auto;
}

.home-nav__link {
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 600;
  white-space: nowrap;
  background: transparent;
  border: 0;
  padding: 0.35rem 0;
  cursor: pointer;
  font-family: inherit;
}

.home-nav__link:hover,
.home-nav__link.router-link-active {
  color: white;
}

.home-nav__entry {
  position: relative;
  margin-left: 1.65rem;
}

.home-nav__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  border: 0;
  border-radius: 8px;
  padding: 0.55rem 0.85rem 0.55rem 1rem;
  background: #ff9f1c;
  color: #1d3557;
  font: inherit;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
}

.home-nav__cta:hover {
  background: #e9c46a;
}

.home-nav__caret--open {
  transform: rotate(180deg);
}

.home-nav__dropdown {
  position: absolute;
  top: calc(100% + 0.45rem);
  right: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  min-width: 14.5rem;
  padding: 0.35rem;
  border-radius: 10px;
  background: white;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
}

.home-nav__dropdown button {
  border: 0;
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
  background: transparent;
  color: #1d3557;
  font: inherit;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.home-nav__dropdown button:hover {
  background: #f7f4ee;
}

@media (max-width: 768px) {
  .home-nav__bar {
    padding: 0 0.75rem;
    gap: 0.35rem;
    min-width: 0;
  }

  .home-nav__brand {
    min-width: 0;
  }

  .home-nav__brand-name {
    display: none;
  }

  .home-nav__toggle {
    display: inline-flex;
    flex-shrink: 0;
    margin-left: 0.15rem;
    padding: 0.35rem 0.15rem;
    font-size: 0.88rem;
  }

  .home-nav__toggle-label {
    max-width: 3.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .home-nav__links {
    display: none;
    position: absolute;
    top: 4rem;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0.15rem;
    margin-left: 0;
    max-height: calc(100vh - 4rem);
    overflow: auto;
    padding: 0.35rem 1rem 1rem;
    background: #1d3557;
    box-shadow: 0 16px 24px rgba(0, 0, 0, 0.2);
  }

  .home-nav__links--open {
    display: flex;
  }

  .home-nav__link {
    text-align: left;
    width: 100%;
  }

  .home-nav__entry {
    margin-left: auto;
    flex-shrink: 0;
  }

  .home-nav__cta {
    width: auto;
    padding: 0.45rem 0.55rem 0.45rem 0.7rem;
    font-size: 0.85rem;
  }
}
</style>
